// src/pages/social/NotificationsPage.jsx
import React, { useEffect, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaTrash, FaBell } from 'react-icons/fa';
import { UserContext } from '../../context/userContext';
import { NotificationContext } from '../../context/notificationContext';
import NotificationItem from '../../components/social/NotificationItem';
import Loader from '../../components/common/Loader';

const NotificationsPage = () => {
  const { currentUser } = useContext(UserContext);
  const { 
    notifications, 
    loading, 
    error, 
    fetchNotifications, 
    markAllAsRead 
  } = useContext(NotificationContext);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();

  // Redirect if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  // Load notifications
  useEffect(() => {
    if (currentUser) {
      fetchNotifications(page);
    }
  }, [currentUser, page, fetchNotifications]);

  // Check if there are more notifications to load
  useEffect(() => {
    if (notifications.length < page * 20) {
      setHasMore(false);
    } else {
      setHasMore(true);
    }
  }, [notifications, page]);

  // Handle mark all as read
  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  // Load more notifications
  const handleLoadMore = () => {
    setPage(prevPage => prevPage + 1);
  };

  if (!currentUser) {
    return null; // Or loading state while redirecting
  }

  return (
    <section className="py-6">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          
          {notifications.length > 0 && (
            <button 
              className="inline-flex items-center text-sm text-gray-600 hover:text-primary transition-colors"
              onClick={handleMarkAllAsRead}
            >
              <FaCheck className="mr-2" /> Mark all as read
            </button>
          )}
        </div>
        
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading && notifications.length === 0 ? (
            <div className="p-12 flex justify-center">
              <Loader />
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-600">
              <div className="mb-4">
                <svg className="w-12 h-12 mx-auto text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p>{error}</p>
              <button 
                className="mt-4 px-4 py-2 bg-gray-200 rounded-md text-gray-800 hover:bg-gray-300 transition-colors"
                onClick={() => fetchNotifications(1)}
              >
                Try Again
              </button>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-12 text-center">
              <div className="mb-4 flex justify-center">
                <FaBell className="text-gray-300" size={64} />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No notifications yet</h3>
              <p className="text-gray-600">When you get notifications, they'll show up here.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map(notification => (
                <NotificationItem 
                  key={notification._id} 
                  notification={notification} 
                />
              ))}
              
              {hasMore && (
                <div className="p-4 text-center">
                  <button 
                    className="px-6 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 transition-colors"
                    onClick={handleLoadMore}
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading...
                      </span>
                    ) : 'Load More'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default NotificationsPage;